import { yupResolver } from '@hookform/resolvers/yup';
import { ClassDefinitionDto, CustomClassFinchMappingDto } from '@zorro/clients';
import { getFieldRequirementsFromClassDefinitions } from '@zorro/common-utils';
import { useDrawerFooter } from '@zorro/design-system/v2/Drawer';
import { Link } from '@zorro/design-system/v2/Link';
import { List } from '@zorro/design-system/v2/List';
import { TextInput } from '@zorro/design-system/v2/TextInput';
import { Textarea } from '@zorro/design-system/v2/Textarea';
import { Title } from '@zorro/design-system/v2/Title';
import { getNowAsDate } from '@zorro/shared/formatters';
import { FormErrorMessage, Grid, Stack, Text } from '@zorro/web/design-system';
import { FormFooter, FullPageLoader } from '@zorro/web/shared-ui';
import {
  extractCustomClassCategories,
  useForm,
  useMonolithMutation,
  useMonolithQuery,
} from '@zorro/web/ui-utils';
import { ReactNode, useEffect, useMemo } from 'react';
import { Controller, useFieldArray } from 'react-hook-form';

import { useAnalytics } from '../Analytics';
import { InformationBoxComponent } from '../InformationBox/InformationBoxComponent';
import {
  FinchEligibilityFormFields,
  getFormSchema,
} from './FinchEligibilityFormUtils';

type Props = {
  onCloseEligibilityDrawerAndPopUpModal: () => void;
  onCloseAfterFormSubmit: () => void;
  finchEmployerId: string;
  employerId: string;
  classDefinitions: ClassDefinitionDto[];
  isFetchingClassDefinitions: boolean;
  onDirtyChange: (isDirty: boolean) => void;
};

const getInstructionsContent = (sendEvent: () => void): ReactNode => {
  return (
    <>
      <Text>
        Define eligibility by combining field names and values with logical
        operators.
      </Text>
      <List size="sm" pl="lg">
        <List.Item>== to check if a field equals a value</List.Item>
        <List.Item>&& (AND) when all conditions must be true</List.Item>
        <List.Item>|| (OR) when at least one condition must be met</List.Item>
        <List.Item>
          Enclose text values in single quotes (e.g. "active")
        </List.Item>
        <List.Item>
          Use parentheses () to group logic and define the order of operations
        </List.Item>
        <List.Item>
          Boolean values must be written as true or false (lowercase, without
          quotes)
        </List.Item>
      </List>
      <Link
        href="https://github.com/TomFrost/jexl#unary-operators"
        target="_blank"
        rel="noopener noreferrer"
        onClick={() => {
          sendEvent();
        }}
      >
        Read more about syntax options
      </Link>
    </>
  );
};

const getSupportedFields = (): ReactNode => {
  return (
    <List size="sm" pl="lg">
      <List.Item>
        income.unit - Optional response: yearly, quarterly, monthly,
        semi_monthly, bi_weekly, weekly, daily, hourly, fixed, “ “.
      </List.Item>
      <List.Item>
        employment.type - Optional response: employee, contractor, " ".
      </List.Item>
      <List.Item>
        employment.subtype - Optional response: full_time, intern, part_time,
        temp, seasonal, individual_contractor, " ".
      </List.Item>
      <List.Item>custom_fields.{'{field_name}'}</List.Item>
      <Text>
        Optional response: the value coming back for each custom field.
      </Text>
    </List>
  );
};

const INSTRUCTIONS_TITLE =
  'Instructions how to write the eligibility criteria and field conditions ';

export const FinchEligibilityForm = ({
  onCloseEligibilityDrawerAndPopUpModal,
  onCloseAfterFormSubmit,
  finchEmployerId,
  employerId,
  classDefinitions,
  isFetchingClassDefinitions,
  onDirtyChange,
}: Props) => {
  const { sendEvent } = useAnalytics();
  const { tryMutate: updateFinchEmployerEligibility } = useMonolithMutation({
    method: 'finchControllerUpsertEmployerEligibility',
  });
  const {
    data: finchEmployerEligibilityData,
    isFetching: isFetchingFinchEmployerEligibilityData,
  } = useMonolithQuery({
    method: 'finchControllerGetEmployerEligibility',
    params: [finchEmployerId],
  });
  const { hasWageType, hasEmploymentType } =
    getFieldRequirementsFromClassDefinitions(
      classDefinitions.flatMap((classDefinition) => classDefinition.items ?? [])
    );
  const customClassCategoryNames = extractCustomClassCategories(
    classDefinitions,
    getNowAsDate().getFullYear(),
    true
  );
  const customClassCategoriesDefaults = customClassCategoryNames.map(
    (name) => ({
      name,
      value:
        finchEmployerEligibilityData?.customClassMappings?.find(
          (mapping: CustomClassFinchMappingDto) =>
            mapping.categoryNameZorro === name
        )?.expression ?? '',
    })
  );
  const { control, getValues, formState } = useForm<FinchEligibilityFormFields>(
    {
      mode: 'all',
      resolver: yupResolver(getFormSchema(hasWageType, hasEmploymentType)),
      defaultValues: {
        eligibilityLogic: finchEmployerEligibilityData?.eligibilityLogic ?? '',
        fullTimeEligibilityLogic:
          finchEmployerEligibilityData?.fullTimeMapping ?? '',
        partTimeEligibilityLogic:
          finchEmployerEligibilityData?.partTimeMapping ?? '',
        salaryEligibilityLogic:
          finchEmployerEligibilityData?.salaryMapping ?? '',
        hourlyEligibilityLogic:
          finchEmployerEligibilityData?.hourlyMapping ?? '',
        customClassCategories: customClassCategoriesDefaults,
      },
    }
  );

  const { fields } = useFieldArray({
    control,
    name: 'customClassCategories',
  });

  const { errors, isValid, isDirty } = formState;

  const handleSave = async () => {
    if (!finchEmployerId) {
      return;
    }
    const formValues = getValues();
    const payload = {
      employerId,
      eligibilityLogic: formValues.eligibilityLogic,
      fullTimeMapping: formValues.fullTimeEligibilityLogic || null,
      partTimeMapping: formValues.partTimeEligibilityLogic || null,
      salaryMapping: formValues.salaryEligibilityLogic || null,
      hourlyMapping: formValues.hourlyEligibilityLogic || null,
      customClassMappings: formValues.customClassCategories?.map((cat) => ({
        categoryNameZorro: cat.name,
        expression: cat.value,
      })),
    };
    const result = await updateFinchEmployerEligibility([
      finchEmployerId,
      payload,
    ]);
    if (result.isOk()) {
      void sendEvent('finch_eligibility_form_save_success_and_close_form', {
        employerId,
      });
      onCloseAfterFormSubmit();
    }
  };

  const drawerFooter = useMemo(
    () => (
      <FormFooter
        withPaper={false}
        paperProps={{
          pos: 'sticky',
          // eslint-disable-next-line id-length
          p: '1rem 2rem',
          // eslint-disable-next-line id-length
          w: '100%',
        }}
        primaryLabel="Save"
        primaryButtonProps={{
          onClick: async () => {
            void sendEvent('finch_eligibility_form_click_save', {
              employerId,
            });
            await handleSave();
          },
          disabled: !isValid || !isDirty,
        }}
        secondaryLabel="Cancel"
        secondaryButtonProps={{
          onClick: onCloseEligibilityDrawerAndPopUpModal,
        }}
      />
    ),
    [
      sendEvent,
      handleSave,
      isValid,
      isDirty,
      onCloseEligibilityDrawerAndPopUpModal,
      employerId,
    ]
  );
  useDrawerFooter(drawerFooter);

  useEffect(() => {
    onDirtyChange(isDirty);
  }, [isDirty, onDirtyChange]);

  return isFetchingClassDefinitions ||
    isFetchingFinchEmployerEligibilityData ? (
    <FullPageLoader />
  ) : (
    <Stack mih="calc(100vh - 100px)" p="0 2rem 2rem" gap={0}>
      <Grid>
        <Grid.Col span={12}>
          <Stack gap="sm">
            <Title order={2}>Eligibility logic:</Title>
            <Text>Logic expression for benefit eligibility*</Text>
            <Controller
              control={control}
              name="eligibilityLogic"
              render={({ field }) => (
                <Textarea
                  {...field}
                  minRows={2}
                  autosize
                  placeholder="Example: employment.subtype == 'full_time' && income.unit == 'hourly'"
                />
              )}
            />
            <FormErrorMessage fieldName="eligibilityLogic" errors={errors} />
          </Stack>
        </Grid.Col>

        <Grid.Col span={12}>
          <Title order={2}>Employment type</Title>
        </Grid.Col>

        <Grid.Col span={12}>
          <Stack gap="sm">
            <Controller
              control={control}
              name="fullTimeEligibilityLogic"
              render={({ field }) => (
                <TextInput
                  {...field}
                  required={hasEmploymentType}
                  label="Full time"
                  placeholder="Example: employment.subtype == 'full_time'"
                />
              )}
            />
            <FormErrorMessage
              fieldName="fullTimeEligibilityLogic"
              errors={errors}
            />
          </Stack>
        </Grid.Col>

        <Grid.Col span={12}>
          <Stack gap="sm">
            <Controller
              control={control}
              name="partTimeEligibilityLogic"
              render={({ field }) => (
                <TextInput
                  {...field}
                  required={hasEmploymentType}
                  label="Part time"
                  placeholder="Example: employment.subtype == 'part_time'"
                />
              )}
            />
            <FormErrorMessage
              fieldName="partTimeEligibilityLogic"
              errors={errors}
            />
          </Stack>
        </Grid.Col>

        <Grid.Col span={12}>
          <Title order={2}>Wage type</Title>
        </Grid.Col>

        <Grid.Col span={12}>
          <Stack gap="sm">
            <Controller
              control={control}
              name="salaryEligibilityLogic"
              render={({ field }) => (
                <TextInput
                  {...field}
                  required={hasWageType}
                  label="Salary"
                  placeholder="Example: income.unit == 'yearly' || income.unit == 'monthly'"
                />
              )}
            />
            <FormErrorMessage
              fieldName="salaryEligibilityLogic"
              errors={errors}
            />
          </Stack>
        </Grid.Col>

        <Grid.Col span={12}>
          <Stack gap="sm">
            <Controller
              control={control}
              name="hourlyEligibilityLogic"
              render={({ field }) => (
                <TextInput
                  {...field}
                  required={hasWageType}
                  label="Hourly"
                  placeholder="Example: income.unit == 'hourly'"
                />
              )}
            />
            <FormErrorMessage
              fieldName="hourlyEligibilityLogic"
              errors={errors}
            />
          </Stack>
        </Grid.Col>

        {fields.length > 0 && (
          <>
            <Grid.Col span={12}>
              <Title order={2}>Custom class category</Title>
            </Grid.Col>
            {fields.map((item, index) => {
              return (
                <Grid.Col span={12} key={item.id}>
                  <Stack gap="xs">
                    <Controller
                      control={control}
                      name={`customClassCategories.${index}.value`}
                      render={({ field }) => (
                        <TextInput
                          {...field}
                          required
                          label={item.name}
                          placeholder={`Example: custom_fields.${item.name} == 'ABC'`}
                        />
                      )}
                    />
                    <FormErrorMessage
                      fieldName={`customClassCategories.${index}.value`}
                      errors={errors}
                    />
                  </Stack>
                </Grid.Col>
              );
            })}
          </>
        )}

        <Grid.Col span={12}>
          <InformationBoxComponent
            title={INSTRUCTIONS_TITLE}
            items={[
              getInstructionsContent(
                () =>
                  void sendEvent(
                    'finch_eligibility_form_read_more_about_syntax_options',
                    { employerId }
                  )
              ),
            ]}
          />
        </Grid.Col>

        <Grid.Col span={12}>
          <InformationBoxComponent
            title="Supported fields"
            items={[getSupportedFields()]}
          />
        </Grid.Col>
      </Grid>
    </Stack>
  );
};
